// frontend/src/pages/Socrates/components/TemplateManagement/index.js

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faSearch, faFilter, 
  faCog, faUsers, faGlobe 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useTemplate } from '../../contexts/TemplateContext';
import SystemTemplateList from './SystemTemplateList';
import UserTemplateList from './UserTemplateList';
import TemplateForm from './TemplateForm';
import TemplateDetail from './TemplateDetail';
import SocratesModal from '../common/SocratesModal';
import styles from './TemplateManagement.module.css';

const TemplateManagement = () => {
  const { t } = useLanguage();
  const { 
    systemTemplates, 
    userTemplates, 
    selectedTemplate, 
    isFormOpen, 
    formMode,
    openCreateForm, 
    closeForm,
    setSelectedTemplate
  } = useTemplate();
  
  const [activeTab, setActiveTab] = useState('system');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Filter templates based on search and status
  const filterTemplates = (templates) => {
    return templates.filter(template => {
      const matchesSearch = template.template_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           template.template_description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || 
                           (filterStatus === 'active' && template.is_active === 'true') ||
                           (filterStatus === 'inactive' && template.is_active !== 'true');
      
      return matchesSearch && matchesStatus;
    });
  };

  const filteredSystemTemplates = filterTemplates(systemTemplates);
  const filteredUserTemplates = filterTemplates(userTemplates);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    // Clear selected template after a short delay to prevent UI flicker
    setTimeout(() => {
      setSelectedTemplate(null);
    }, 300);
  };

  return (
    <div className={styles.templateManagement}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>{t('plato_template_management')}</h1>
          <p className={styles.subtitle}>{t('manage_system_and_user_templates')}</p>
        </div>
        <div className={styles.headerRight}>
          <button 
            className={styles.createButton}
            onClick={openCreateForm}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{t('create_template')}</span>
          </button>
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            placeholder={t('search_templates')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filterContainer}>
          <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">{t('all_templates')}</option>
            <option value="active">{t('active_templates')}</option>
            <option value="inactive">{t('inactive_templates')}</option>
          </select>
        </div>
      </div>

      <div className={styles.tabs}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'system' ? styles.active : ''}`}
          onClick={() => setActiveTab('system')}
        >
          <FontAwesomeIcon icon={faGlobe} />
          <span>{t('system_templates')}</span>
          <span className={styles.tabCount}>({filteredSystemTemplates.length})</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'user' ? styles.active : ''}`}
          onClick={() => setActiveTab('user')}
        >
          <FontAwesomeIcon icon={faUsers} />
          <span>{t('user_templates')}</span>
          <span className={styles.tabCount}>({filteredUserTemplates.length})</span>
        </button>
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'system' && (
          <SystemTemplateList 
            templates={filteredSystemTemplates}
            onTemplateSelect={handleTemplateSelect}
          />
        )}
        {activeTab === 'user' && (
          <UserTemplateList 
            templates={filteredUserTemplates}
            onTemplateSelect={handleTemplateSelect}
          />
        )}
      </div>

      {/* Template Form Modal */}
      <SocratesModal
        isOpen={isFormOpen}
        onClose={closeForm}
        title={formMode === 'create' ? t('create_template') : t('edit_template')}
        fullScreen={true}
      >
        <TemplateForm />
      </SocratesModal>

      {/* Template Detail Modal */}
      <SocratesModal
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
        title={t('template_details')}
        size="large"
      >
        <TemplateDetail 
          template={selectedTemplate} 
          onClose={handleCloseDetail}
        />
      </SocratesModal>
    </div>
  );
};

export default TemplateManagement;